import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Zap, Edit01, RefreshCcw01 } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { AppDialog } from "@/components/app/AppDialog";
import { Tabs } from "@/components/application/tabs/tabs";
import { ApiError } from "@/lib/apiClient";
import {
  useConnectors,
  useTestConnection,
  useUpdateConnector,
  useResetConnector,
  type ConnectorConfig,
  type TestResult,
} from "@/lib/connectors";
import { CONNECTOR_FIELDS, type ConnField } from "./connectorFields";
import { SectionHeader } from "@/components/application/section-headers/section-headers";
import { AppSelect } from "@/components/app/AppSelect";

const inputCls =
  "w-full rounded-lg bg-primary px-3 py-2.5 text-sm text-primary shadow-xs ring-1 ring-primary outline-none focus:ring-2 focus:ring-brand";

function apiErrText(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { message?: string; error?: string } | string | null;
    if (typeof body === "string" && body) return body;
    if (body && typeof body === "object") return body.message ?? body.error ?? e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

/**
 * Konektory — list + test + **editace** konfigurace (enabled, config, credentials).
 * Credentials jsou maskované (`****`); prázdné/maskované pole server při uložení
 * zachová (needí tajemství). Reset vrátí výchozí hodnoty.
 */
export function ConnectorsPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useConnectors();

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("connectors.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("connectors.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      {isLoading && <LoadingIndicator />}
      {isError && <p className="text-sm text-error-primary">{t("common.loadError")}</p>}

      {data && (
        <div className="flex flex-col gap-4">
          {data.length === 0 && <p className="text-sm text-tertiary">{t("connectors.none")}</p>}
          {data.map((c) => (
            <ConnectorCard key={c.connectorType} connector={c} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Výsledek testu připojení. Tři stavy, protože „něco odpovědělo" ≠ „konektor funguje":
 * `DEGRADED` je odpověď od někoho jiného než cílového serveru (typicky firemní proxy,
 * která odmítne routu vlastní chybovou stránkou) — dřív se tvářila zeleně.
 */
function TestResultPanel({ result }: { result: TestResult }) {
  const { t } = useTranslation();
  const status = result.status ?? (result.success ? "UP" : "DOWN");
  const tone =
    status === "UP" ? "text-success-primary" : status === "DEGRADED" ? "text-warning-primary" : "text-error-primary";
  const icon = status === "UP" ? "✓" : status === "DEGRADED" ? "!" : "✗";

  // Titulek i důvod se skládají ze stavu a strojového kódu, ne ze serverových vět:
  // `message` / `detail` jsou anglické a jsou součástí publikovaného API, takže se
  // nepřekládají na serveru. `error` je jediná výjimka — technický text výjimky.
  const headline =
    status === "UP"
      ? t("connectors.ok")
      : status === "DEGRADED"
        ? t("connectors.degraded")
        : (result.error ?? t("connectors.fail"));

  const routeText =
    result.proxyTarget != null
      ? t("connectors.route.proxy", { target: result.proxyTarget })
      : result.route === "direct"
        ? t("connectors.route.direct")
        : result.route;

  const detailText = result.detailCode
    ? t(`connectors.detail.${result.detailCode}`, { status: result.httpStatus })
    : result.detail;

  return (
    <div className="mx-4 mb-3 flex flex-col gap-1 rounded-lg bg-secondary px-3 py-2 text-sm ring-1 ring-secondary">
      <div className="flex flex-wrap items-center gap-2">
        <Badge type="pill-color" size="sm" color={status === "UP" ? "success" : status === "DEGRADED" ? "warning" : "error"}>
          {status}
        </Badge>
        <span className={tone}>
          {icon} {headline}
          {result.responseTimeMs != null ? ` (${result.responseTimeMs} ms)` : ""}
        </span>
      </div>
      {(routeText || result.httpStatus != null || detailText) && (
        <span className="text-xs text-tertiary">
          {[
            routeText ? `${t("connectors.route")}: ${routeText}` : null,
            result.httpStatus != null ? `HTTP ${result.httpStatus}` : null,
            detailText,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      )}
    </div>
  );
}

function ConnectorCard({ connector }: { connector: ConnectorConfig }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const test = useTestConnection();
  const toggle = useUpdateConnector();
  const [result, setResult] = useState<TestResult | null>(null);

  const runTest = async () => {
    setResult(null);
    try {
      setResult(await test.mutateAsync(connector.connectorType));
    } catch {
      setResult({ success: false, message: null, responseTimeMs: null, connectorType: connector.connectorType, error: t("common.loadError") });
    }
  };

  // Inline enable/disable — config i (maskované) credentials pošleme beze změny.
  const toggleEnabled = () =>
    toggle.mutate({
      connectorType: connector.connectorType,
      body: { enabled: !connector.enabled, config: connector.config ?? {}, credentials: connector.credentials ?? null },
    });

  return (
    <div className="rounded-xl bg-primary ring-1 ring-secondary">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-3">
          <span className="text-md font-semibold text-primary">{connector.connectorType}</span>
          <button
            type="button"
            onClick={toggleEnabled}
            disabled={toggle.isPending}
            title={connector.enabled ? t("connectors.disable") : t("connectors.enable")}
            className="cursor-pointer disabled:opacity-50"
          >
            <Badge type="pill-color" size="sm" color={connector.enabled ? "success" : "gray"}>
              {connector.enabled ? t("connectors.enabled") : t("connectors.disabled")}
            </Badge>
          </button>
          <span className="text-xs text-tertiary">v{connector.configVersion}</span>
        </div>
        <div className="flex items-center gap-2">
          <span title={!connector.enabled ? t("connectors.enableFirst") : undefined}>
            <Button
              size="sm"
              color="secondary"
              iconLeading={Zap}
              isDisabled={test.isPending || !connector.enabled}
              onPress={() => void runTest()}
            >
              {test.isPending ? t("connectors.testing") : t("connectors.test")}
            </Button>
          </span>
          <Button size="sm" color="secondary" iconLeading={Edit01} onPress={() => setEditing(true)}>
            {t("common.edit")}
          </Button>
          <Button size="sm" color="tertiary" iconLeading={RefreshCcw01} onPress={() => setResetting(true)}>
            {t("connectors.reset")}
          </Button>
          <Button
            size="sm"
            color="tertiary"
            iconLeading={open ? ChevronDown : ChevronRight}
            onPress={() => setOpen((o) => !o)}
          >
            {t("connectors.config")}
          </Button>
        </div>
      </div>

      {result && <TestResultPanel result={result} />}

      {open && (
        <pre className="mx-4 mb-4 max-h-96 overflow-auto rounded-lg bg-secondary p-3 font-mono text-xs text-secondary ring-1 ring-secondary">
          {JSON.stringify(connector.config, null, 2)}
        </pre>
      )}

      {editing && <EditConnectorDialog connector={connector} onClose={() => setEditing(false)} />}
      {resetting && <ResetConnectorDialog connector={connector} onClose={() => setResetting(false)} />}
    </div>
  );
}

function ConnFieldInput({ field, value, onChange }: { field: ConnField; value: unknown; onChange: (v: unknown) => void }) {
  if (field.type === "checkbox") {
    return (
      <label className="flex items-center gap-2 text-sm text-secondary">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="size-4" />
        {field.label}
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-tertiary">{field.label}</span>
        {/* py-2.5 drží stejnou výšku jako sousední <input> v tomto formuláři. */}
        <AppSelect className="py-2.5" value={value == null ? "" : String(value)} onChange={(e) => onChange(e.target.value)}>
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </AppSelect>
        {field.hint && <span className="text-xs text-tertiary">{field.hint}</span>}
      </label>
    );
  }
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-tertiary">{field.label}</span>
      <input
        className={inputCls}
        type={field.type === "password" ? "password" : field.type === "number" ? "number" : "text"}
        value={value == null ? "" : String(value)}
        onChange={(e) => onChange(field.type === "number" ? (parseInt(e.target.value, 10) || 0) : e.target.value)}
        spellCheck={false}
      />
      {field.hint && <span className="text-xs text-tertiary">{field.hint}</span>}
    </label>
  );
}

function EditConnectorDialog({ connector, onClose }: { connector: ConnectorConfig; onClose: () => void }) {
  const { t } = useTranslation();
  const m = useUpdateConnector();
  const schema = CONNECTOR_FIELDS[connector.connectorType];
  const [enabled, setEnabled] = useState(connector.enabled);
  const [err, setErr] = useState<string | null>(null);

  // Typovaný stav (start z existujícího config/credentials → zachová i klíče mimo schéma).
  const [config, setConfig] = useState<Record<string, unknown>>(() =>
    JSON.parse(JSON.stringify(connector.config ?? {})) as Record<string, unknown>,
  );
  const [creds, setCreds] = useState<Record<string, unknown>>(() => ({ ...(connector.credentials ?? {}) }));

  // JSON fallback (neznámý typ konektoru).
  const [configText, setConfigText] = useState(JSON.stringify(connector.config ?? {}, null, 2));
  const hasCreds = connector.credentials && Object.keys(connector.credentials).length > 0;
  const [credText, setCredText] = useState(hasCreds ? JSON.stringify(connector.credentials, null, 2) : "");

  const setTop = (key: string, v: unknown) => setConfig((p) => ({ ...p, [key]: v }));
  const getTop = (key: string) => config[key];
  const setNested = (section: string, key: string, v: unknown) =>
    setConfig((p) => ({ ...p, [section]: { ...((p[section] as Record<string, unknown>) ?? {}), [key]: v } }));
  const getNested = (section: string, key: string) => ((config[section] as Record<string, unknown>) ?? {})[key];
  const setCred = (key: string, v: unknown) => setCreds((p) => ({ ...p, [key]: v }));

  const submit = async () => {
    setErr(null);
    let payloadConfig: Record<string, unknown>;
    let payloadCreds: Record<string, unknown> | null = null;
    if (schema) {
      payloadConfig = config;
      // Credentials: pošli jen neprázdné (maskované `****` server zachová, prázdné vynech).
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(creds)) {
        const s = v == null ? "" : String(v);
        if (s !== "") out[k] = v;
      }
      payloadCreds = Object.keys(out).length > 0 ? out : null;
    } else {
      try {
        payloadConfig = JSON.parse(configText || "{}");
      } catch {
        setErr(t("connectors.invalidConfigJson"));
        return;
      }
      if (credText.trim()) {
        try {
          payloadCreds = JSON.parse(credText);
        } catch {
          setErr(t("connectors.invalidCredsJson"));
          return;
        }
      }
    }
    try {
      await m.mutateAsync({ connectorType: connector.connectorType, body: { enabled, config: payloadConfig, credentials: payloadCreds } });
      onClose();
    } catch (e) {
      setErr(apiErrText(e));
    }
  };

  // Taby: Obecné (top-level config) + jedna záložka na každou nested sekci +
  // Credentials (top-level). Rozdělením do tabů zmizí dlouhé scrollování.
  const nestedEntries = schema ? Object.entries(schema.nested) : [];
  const tabItems = schema
    ? [
        ...(schema.config.length > 0 ? [{ id: "__general", label: t("connectors.tabGeneral") }] : []),
        ...nestedEntries.map(([section, sec]) => ({ id: section, label: sec.tabLabel ?? sec.title })),
        ...(schema.credentials.length > 0 ? [{ id: "__credentials", label: t("connectors.credentials") }] : []),
      ]
    : [];

  const gridCls = "grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2";

  return (
    <AppDialog
      isOpen
      onClose={onClose}
      size="2xl"
      title={`${t("common.edit")} — ${connector.connectorType}`}
      description={t("connectors.editHint")}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={m.isPending}>{t("common.cancel")}</Button>
          <Button color="primary" onPress={() => void submit()} isDisabled={m.isPending}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-5 pt-1">
        {/* Enabled toggle — vždy viditelný nad taby */}
        <label className="flex w-fit cursor-pointer items-center gap-2.5 rounded-lg bg-secondary px-3.5 py-2.5 ring-1 ring-secondary">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="size-4" />
          <span className="text-sm font-medium text-secondary">{t("connectors.enabledLabel")}</span>
        </label>

        {schema ? (
          <Tabs>
            <Tabs.List type="button-border" size="sm" items={tabItems} className="flex-wrap">
              {(item) => <Tabs.Item {...item} />}
            </Tabs.List>

            {/* Pevná výška — okno neskáče při přepínání tabů (krátký tab má prostor navíc). */}
            <div className="mt-5 h-[clamp(300px,46vh,460px)] overflow-y-auto pr-1">
              {schema.config.length > 0 && (
                <Tabs.Panel id="__general">
                  <div className={gridCls}>
                    {schema.config.map((f) => (
                      <ConnFieldInput key={f.key} field={f} value={getTop(f.key)} onChange={(v) => setTop(f.key, v)} />
                    ))}
                  </div>
                </Tabs.Panel>
              )}

              {nestedEntries.map(([section, sec]) => (
                <Tabs.Panel key={section} id={section}>
                  <div className={gridCls}>
                    {sec.fields.map((f) => (
                      <ConnFieldInput key={f.key} field={f} value={getNested(section, f.key)} onChange={(v) => setNested(section, f.key, v)} />
                    ))}
                    {(sec.credentials ?? []).map((f) => (
                      <ConnFieldInput key={f.key} field={f} value={creds[f.key]} onChange={(v) => setCred(f.key, v)} />
                    ))}
                  </div>
                </Tabs.Panel>
              ))}

              {schema.credentials.length > 0 && (
                <Tabs.Panel id="__credentials">
                  <div className={gridCls}>
                    {schema.credentials.map((f) => (
                      <ConnFieldInput key={f.key} field={f} value={creds[f.key]} onChange={(v) => setCred(f.key, v)} />
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-tertiary">{t("connectors.credsHint")}</p>
                </Tabs.Panel>
              )}
            </div>
          </Tabs>
        ) : (
          <div className="flex flex-col gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-secondary">{t("connectors.configJson")}</span>
              <textarea className={`${inputCls} h-56 font-mono`} value={configText} onChange={(e) => setConfigText(e.target.value)} spellCheck={false} />
            </label>
            {hasCreds && (
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-secondary">{t("connectors.credsJson")}</span>
                <textarea className={`${inputCls} h-32 font-mono`} value={credText} onChange={(e) => setCredText(e.target.value)} spellCheck={false} />
                <span className="text-xs text-tertiary">{t("connectors.credsHint")}</span>
              </label>
            )}
          </div>
        )}

        {err && <div className="rounded-lg bg-error-primary px-3 py-2 text-sm text-error-primary ring-1 ring-error">{err}</div>}
      </div>
    </AppDialog>
  );
}

function ResetConnectorDialog({ connector, onClose }: { connector: ConnectorConfig; onClose: () => void }) {
  const { t } = useTranslation();
  const m = useResetConnector();
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setErr(null);
    try {
      await m.mutateAsync(connector.connectorType);
      onClose();
    } catch (e) {
      setErr(apiErrText(e));
    }
  };
  return (
    <AppDialog
      isOpen
      onClose={onClose}
      title={t("connectors.reset")}
      description={t("connectors.resetConfirm", { type: connector.connectorType })}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={m.isPending}>{t("common.cancel")}</Button>
          <Button color="primary-destructive" onPress={() => void submit()} isDisabled={m.isPending}>{t("connectors.reset")}</Button>
        </>
      }
    >
      {err && <div className="rounded-lg bg-error-primary px-3 py-2 text-sm text-error-primary ring-1 ring-error">{err}</div>}
    </AppDialog>
  );
}
